import { AccountInfo, PublicClientApplication } from '@azure/msal-browser';
import { AxiosError, AxiosResponse } from 'axios';
import TokenRefreshError from 'errors/TokenRefreshError';
import { SCOPES, loginRequest, msalInstance } from 'msalConfig/msalConfig';
import { anonymousAxiosInstance, axiosInstance } from 'services/api';

export interface JwtTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface ForgotPasswordResponse {
  token: string;
}

type AuthStateListener = (isAuthenticated: boolean) => void;

export class AuthService {
  public isSSOEnabled = false;
  private resetApiState: (() => void) | null = null;

  private activeAccount: AccountInfo | null = null;

  // Until we figure out why MSAL doesn’t cache the token, we’ll handle it manually.
  private cachedIdToken: string | null = null;
  private tokenExpiresAt = 0;
  private isRefreshingMsalCache = false;

  private jwtAccessToken: string | null = null;
  private jwtRefreshToken: string | null = null;

  private isRefreshingJwt = false;
  private failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  private listeners: AuthStateListener[] = [];

  constructor(private msalInstance: PublicClientApplication) {}

  public initialize(isSSOEnabled: boolean, resetApiState: () => void): void {
    this.isSSOEnabled = isSSOEnabled;
    this.resetApiState = resetApiState;

    if (isSSOEnabled) {
      const allAccounts = this.msalInstance.getAllAccounts();

      if (allAccounts.length > 0) {
        this.activeAccount = allAccounts[0];
      }
    } else {
      this.jwtAccessToken = window.localStorage.getItem('jwt_access_token');
      this.jwtRefreshToken = window.localStorage.getItem('jwt_refresh_token');
    }
  }

  public isAuthenticated(): boolean {
    if (this.isSSOEnabled) {
      return !!this.activeAccount;
    }
    return !!this.jwtAccessToken;
  }

  public async getAccessToken(): Promise<string | null> {
    if (this.isSSOEnabled) {
      // Until we figure out why MSAL doesn’t cache the token, we’ll handle it manually.
      // return this.getMsalToken();
      return this.getCachedMsalIdToken();
    } else {
      return this.jwtAccessToken;
    }
  }

  /**
   * Handles the authentication response from MSAL after a `loginRedirect`.
   * This function must be called once during application initialization.
   * It checks the URL for a code/token, completes the sign-in process,
   * and establishes the user's session. If it's a normal page load,
   * it does nothing and resolves instantly.
   */
  public async handleLoginRedirect(): Promise<void> {
    if (this.isSSOEnabled) {
      try {
        const response = await this.msalInstance.handleRedirectPromise();

        if (response) {
          this.activeAccount = response.account;
        }
        this.notifyListeners();
      } catch (error) {
        console.error('MSAL redirect handling failed:', error);
      }
    }
  }

  public async login(credentials?: {
    username?: string;
    password?: string;
    language?: string;
  }): Promise<void> {
    if (this.isSSOEnabled) {
      const request = loginRequest(credentials?.language ?? 'en');
      await this.msalInstance.loginRedirect(request);
    } else {
      if (!credentials?.username || !credentials?.password) {
        throw new Error('Username and password are required for JWT login.');
      }
      try {
        const params = new URLSearchParams();
        params.append('grant_type', 'password');
        params.append('username', credentials.username);
        params.append('password', credentials.password);

        const { data }: AxiosResponse<JwtTokenResponse> =
          await axiosInstance.post(
            `${process.env.UI_API_URL}/oauth/token`,
            params,
          );

        this.setJwtSession(data);
        this.notifyListeners();
      } catch (error) {
        console.error('JWT login failed:', error);
        throw error;
      }

      this.resetApiState?.();
    }
  }

  public async logout(): Promise<void> {
    this.cachedIdToken = null;
    this.tokenExpiresAt = 0;

    if (this.isSSOEnabled) {
      const accountToLogout = this.activeAccount;
      this.activeAccount = null;
      await this.msalInstance.logoutRedirect({ account: accountToLogout });
    } else {
      this.clearJwtSession();
      this.notifyListeners();
    }

    this.resetApiState?.();
  }

  public async forgotPassword(email: string): Promise<string> {
    const params = new URLSearchParams();
    params.append('email', email);

    try {
      const { data }: AxiosResponse<ForgotPasswordResponse> =
        await anonymousAxiosInstance.post('/oauth/forgot_password', params);
      const token = data.token;
      if (!token) {
        throw new Error(
          'Token was not returned from forgot_password endpoint.',
        );
      }
      return token;
    } catch (e: any) {
      console.error('Forgot password request failed:', e);
      throw e;
    }
  }

  public async resetPassword(options: {
    token: string;
    code: string;
    newPassword: string;
    email: string;
  }): Promise<void> {
    const { token, code, newPassword, email } = options;

    const params = new URLSearchParams();
    params.append('token', token);
    params.append('code', code);
    params.append('new_password', newPassword);

    try {
      await anonymousAxiosInstance.put(`/oauth/reset_password`, params);

      await this.login({ username: email, password: newPassword });
    } catch (e: any) {
      console.error('Reset password request failed:', e);
      throw e;
    }
  }

  public async handleUnauthorizedError(error: AxiosError): Promise<boolean> {
    const originalRequest = error.config;

    if (this.isSSOEnabled) {
      console.error('MSAL token is invalid or expired. Logging out.');
      this.logout();
      return false;
    }

    if (this.isRefreshingJwt) {
      const canRetry = await new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      })
        .then(() => true)
        .catch(() => false);
      return canRetry;
    }

    (originalRequest as any)._retry = true;
    this.isRefreshingJwt = true;

    const newAccessToken = await this.refreshJwtToken();

    if (newAccessToken) {
      this.isRefreshingJwt = false;
      this.processFailedQueue(null, newAccessToken);
      return true;
    } else {
      this.isRefreshingJwt = false;
      this.processFailedQueue(new Error('Unable to refresh token'), null);
      this.logout();
      return false;
    }
  }

  public onAuthStateChanged(listener: AuthStateListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners(): void {
    const isAuthenticated = this.isAuthenticated();
    this.listeners.forEach(listener => listener(isAuthenticated));
  }

  private async getMsalToken(): Promise<string | null> {
    if (!this.activeAccount) {
      return null;
    }
    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: [process.env.B2C_CLIENT_ID!],
        account: this.activeAccount,
      });
      return response.idToken;
    } catch (error) {
      console.error('MSAL silent token acquisition failed:', error);
      this.logout();
      return null;
    }
  }

  private async getCachedMsalIdToken(): Promise<string | null> {
    const now = Date.now();
    const safetyMargin = 2 * 60 * 1000;

    if (this.cachedIdToken && now < this.tokenExpiresAt - safetyMargin) {
      return this.cachedIdToken;
    }

    return this.refreshMsalIdTokenCache();
  }

  private async refreshMsalIdTokenCache(): Promise<string | null> {
    if (this.isRefreshingMsalCache) {
      return this.cachedIdToken;
    }

    if (!this.activeAccount) {
      return null;
    }

    this.isRefreshingMsalCache = true;

    try {
      const response = await this.msalInstance.acquireTokenSilent({
        scopes: SCOPES,
        account: this.activeAccount,
      });

      this.cachedIdToken = response.idToken;
      this.tokenExpiresAt = ((response.idTokenClaims as any)?.exp || 0) * 1000;

      if (!this.tokenExpiresAt) {
        this.cachedIdToken = null;
        this.tokenExpiresAt = 0;
        return null;
      }

      return this.cachedIdToken;
    } catch (error) {
      console.error(
        'MSAL silent token acquisition failed during cache refresh:',
        error,
      );
      this.cachedIdToken = null;
      this.tokenExpiresAt = 0;
      this.logout();
      return null;
    } finally {
      this.isRefreshingMsalCache = false;
    }
  }

  private processFailedQueue(error: Error | null, token: string | null = null) {
    this.failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private setJwtSession(data: JwtTokenResponse): void {
    this.jwtAccessToken = data.access_token;
    this.jwtRefreshToken = data.refresh_token;
    window.localStorage.setItem('jwt_access_token', data.access_token);
    window.localStorage.setItem('jwt_refresh_token', data.refresh_token);
  }

  private clearJwtSession(): void {
    this.jwtAccessToken = null;
    this.jwtRefreshToken = null;
    window.localStorage.removeItem('jwt_access_token');
    window.localStorage.removeItem('jwt_refresh_token');
  }

  private async refreshJwtToken(): Promise<string | null> {
    try {
      const params = new URLSearchParams();
      params.append('grant_type', 'refresh_token');
      params.append('refresh_token', this.jwtRefreshToken!);

      const { data }: AxiosResponse<JwtTokenResponse> =
        await axiosInstance.post(
          `${process.env.UI_API_URL}/oauth/token`,
          params,
        );

      this.setJwtSession(data);
      return data.access_token;
    } catch (error) {
      throw new TokenRefreshError('Failed to refresh JWT token');
    }
  }
}

export const authService = new AuthService(msalInstance);
